import { FC, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiPlaceholderList } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { RequirementType } from "@miplatform/types";
import {
  filterMaterialsByAttributes,
  filterMaterialsByCategory,
  filterMaterialsBySearch,
} from "@miplatform/utils";
import { mdiPlus } from "@mdi/js";
import { QuantityField, SearchResultsContainer } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { CategoryFilterDrawer } from "../../../../components/FilterDrawer/CategoryFilterDrawer";
import { MaterialCard } from "../../../../components/MaterialCard/MaterialCard";
import { IAddRequirementsData } from "../../schema/addRequirements.schema";
import { useRequirementItemsViewModel } from "./useRequirementItemsViewModel";

const TYPE_LABEL: Record<RequirementType, string> = {
  Inspection: "Inspection",
  Community: "Community",
  Builder: "Builder",
};

export const RequirementItemsTab: FC<RequirementItemsTabProps> = ({
  form,
  extraTabProps,
}) => {
  const { isReadOnly = false } = extraTabProps || {};
  const { classes, cx } = useStyles();
  const { watch, setValue, getValues } = form;
  const type = watch("type");
  const materialIds: string[] = watch("materialIds") ?? [];
  const title = `${TYPE_LABEL[type]} Requirement Items`;

  const {
    isLoading,
    materials,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    debouncedSearch,
    search,
    filteredMaterials,
    openMaterialResults,
    openCategoryDrawer,
    hasFilters,
    requirementItems,
    getMaterials,
    getCategoryFilters,
    handleSearch,
    handleCategoryClick,
    handleAttributeValueClick,
    handleReset,
    handleResetResultContainer,
    handleCloseCategoryDrawer,
    handleAddRemoveData,
    handleUpdateItemQuantity,
    isDataSelected,
    setFilteredMaterials,
    setOpenCategoryDrawer,
    setRequirementItems,
  } = useRequirementItemsViewModel({ setValue, getValues });

  useEffect(() => {
    getCategoryFilters();
    getMaterials();
  }, []);

  useEffect(() => {
    let filtered = [...materials];
    if (selectedCategory?.categoryId) {
      filtered = filterMaterialsByCategory(filtered, selectedCategory);
    }
    if (Object.keys(selectedAttributeValues).length > 0) {
      filtered = filterMaterialsByAttributes(filtered, selectedAttributeValues);
    }
    if (debouncedSearch) {
      filtered = filterMaterialsBySearch(filtered, debouncedSearch);
    }
    setFilteredMaterials(filtered);
  }, [selectedCategory, selectedAttributeValues, debouncedSearch]);

  useEffect(() => {
    if (isReadOnly) {
      handleCloseCategoryDrawer();
    }
  }, [isReadOnly]);

  useEffect(() => {
    if (
      materials.length > 0 &&
      materialIds.length > 0 &&
      requirementItems.length === 0
    ) {
      const restored = materials.filter((m: any) => materialIds.includes(m.id));
      setRequirementItems(restored);
    }
  }, [materials]);

  const addButton = isReadOnly ? null : (
    <MiButton
      component="div"
      title="Add Requirement Item"
      variant={BUTTON_VARIANTS.CONTAINED}
      startIcon={{ path: mdiPlus }}
      onClick={() => setOpenCategoryDrawer(true)}
      disabled={openCategoryDrawer}
      className={classes.addButton}
    />
  );

  const activeItems = requirementItems.filter((item) => !item.removed);
  const isEmpty = activeItems.length === 0;

  return (
    <Box className={classes.container}>
      <Box className={cx(classes.inner, { [classes.innerExpanded]: !isEmpty })}>
        <ConfigAccordionHeader
          title={title}
          customAction={addButton}
          disableCollapse
        >
          {isEmpty && (
            <Box className={classes.placeholderBox}>
              <MiPlaceholderList
                text="There are no records to display"
                helperText="To create, click the + Add Requirement Item button"
              />
            </Box>
          )}
          {activeItems.map((item) => (
            <Box key={item.id} sx={{ mb: 1 }}>
              <MaterialCard
                item={item}
                showPrice
                leftAction={
                  <QuantityField
                    quantity={watch(`materialQuantities.${item.id}`) ?? 1}
                    disabled={isReadOnly}
                    onChange={(qty) => handleUpdateItemQuantity(item.id, qty)}
                  />
                }
                menuData={
                  isReadOnly
                    ? []
                    : [
                        {
                          label: "Remove",
                          action: () => handleAddRemoveData(item),
                        },
                      ]
                }
              />
            </Box>
          ))}
        </ConfigAccordionHeader>
      </Box>

      <SearchResultsContainer
        open={openMaterialResults}
        onClose={handleResetResultContainer}
        alertTitle="Select Item(s) and add to Requirement."
        alertDescription="Select the Link Item button to add the Item to the Requirement."
        storageKey="requirement-items-alert"
        isLoading={isLoading}
        results={filteredMaterials}
        hasFilters={hasFilters}
        handleResetFilterSelection={handleReset}
        renderResults={() =>
          filteredMaterials.map((material) => {
            const isSelected = isDataSelected(material);
            return (
              <MaterialCard
                key={material.id}
                item={material}
                isSelected={isSelected}
                rightAction={
                  isSelected ? null : (
                    <MiButton
                      title="Link Item"
                      variant={BUTTON_VARIANTS.CONTAINED}
                      onClick={() => handleAddRemoveData(material)}
                    />
                  )
                }
              />
            );
          })
        }
      />

      <CategoryFilterDrawer
        open={openCategoryDrawer}
        onClose={handleCloseCategoryDrawer as any}
        title="Filter Items"
        categoryFilterOptions={categoryFilterOptions}
        search={search}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        setSearch={handleSearch}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        handleReset={handleReset}
        handleResetSelection={handleReset}
      />
    </Box>
  );
};

interface RequirementItemsTabProps {
  form: UseFormReturn<IAddRequirementsData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flex: 1,
    height: "100%",
    overflow: "hidden",
    gap: theme.spacing(3),
  },
  inner: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  innerExpanded: {
    "& .MuiAccordion-root": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },
    "& .MuiAccordionDetails-root": {
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
    },
  },
  placeholderBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
  addButton: {
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
}));
